import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpAppsecEditPermission } from '@internal/ksdp-permissions-common';

const REQUESTED_POLL_INTERVAL_MS = 5000;
const IN_PROGRESS_POLL_INTERVAL_MS = 15000;
const CHECKLIST_POLL_INTERVAL_MS = 300000;

type AssessmentStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_CHECKLIST'
  | 'COMPLETED'
  | 'FAILED';

interface StandardFolder {
  folder_id: number;
  folder_name: string;
  project: number;
  project_name: string;
}

interface StandardFoldersResponse {
  ban: string;
  standard_folders: Record<string, StandardFolder[]>;
}

interface AssessmentRequestResponse {
  status?: 'EXISTS';
  request_id: string;
  execution_arn?: string;
}

interface AssessmentSummary {
  request_id: string;
  standard: string;
  requested_by?: string | null;
  status: AssessmentStatus;
  requested_at?: string | null;
  completed_at?: string | null;
  last_completed_step?: string | null;
  error_message?: string | null;
}

interface AssessmentStatusResponse extends AssessmentSummary {
  ban: string;
  jama_folder_id: number | string;
  started_at?: string | null;
  reuse_previous_assessment: boolean;
  reused_from_request_id?: string | null;
}

interface ComplianceReport {
  request_id: string;
  s3_key: string | null;
  timestamp: string;
  standard: string;
  jama_folder_id: number;
  requested_by?: string | null;
  url?: string | null;
  compliance_score: number | null;
  reuse_previous_assessment: boolean;
  reused_from_request_id?: string | null;
}

interface ComplianceReportsResponse {
  reports: ComplianceReport[];
  active_assessments?: AssessmentStatusResponse[];
  failed_assessments?: AssessmentStatusResponse[];
}

interface ViewError {
  message: string;
  stage: string;
  requestId?: string;
}

export interface OnsiteComplianceAssessmentViewProps {
  ban: string;
  onBack: () => void;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Not available';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function statusLabel(status: AssessmentStatus): string {
  switch (status) {
    case 'REQUESTED':
      return 'Assessment requested';
    case 'IN_PROGRESS':
      return 'Assessment in progress';
    case 'WAITING_FOR_CHECKLIST':
      return 'Waiting for checklist approval';
    case 'COMPLETED':
      return 'Completed';
    case 'FAILED':
      return 'Failed';
    default:
      return status;
  }
}

interface ReportDownloadActionProps {
  requestId: string;
  location: 'latest' | 'history';
  downloadingActionId: string | null;
  downloadErrors: Record<string, string>;
  onDownload: (requestId: string, actionId: string) => void;
}

function ReportDownloadAction({
  requestId,
  location,
  downloadingActionId,
  downloadErrors,
  onDownload,
}: ReportDownloadActionProps) {
  const actionId = `${location}-${requestId}`;
  const downloading = downloadingActionId === actionId;

  return (
    <div className="onsite-compliance-report-action">
      <button
        type="button"
        className="action-button"
        onClick={() => onDownload(requestId, actionId)}
        disabled={downloading}
      >
        {downloading ? 'Preparing report...' : 'Download report'}
      </button>

      {downloadErrors[actionId] && (
        <span className="onsite-compliance-download-message" role="alert">
          {downloadErrors[actionId]}
        </span>
      )}
    </div>
  );
}

async function getErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const body = await response.json();
    const errorValue = body.error ?? body.message;

    if (typeof errorValue === 'string') {
      return errorValue;
    }

    if (
      errorValue &&
      typeof errorValue === 'object' &&
      typeof errorValue.message === 'string'
    ) {
      return errorValue.message;
    }

    return fallback;
  } catch {
    return fallback;
  }
}

function getComplianceScoreColor(score: number): string {
  if (score >= 80) return 'var(--kone-success)';
  if (score >= 60) return 'var(--kone-warning)';
  return 'var(--kone-error)';
}

function ComplianceScore({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const color = getComplianceScoreColor(clamped);

  return (
    <div
      className="compliance-score-circle"
      style={{
        background: `conic-gradient(
          ${color} ${clamped}%,
          var(--kone-border) 0
        )`,
      }}
      role="img"
      aria-label={`Compliance score ${Math.round(clamped)} percent`}
    >
      <div className="compliance-score-circle-inner">
        <span className="compliance-score-value">{Math.round(clamped)}</span>
        <span className="compliance-score-unit">%</span>
      </div>
    </div>
  );
}

export function OnsiteComplianceAssessmentView({
  ban,
  onBack,
}: OnsiteComplianceAssessmentViewProps) {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // UI-only gate; requesting an assessment is enforced by the backend router.
  const { allowed: canEdit } = usePermission({
    permission: ksdpAppsecEditPermission,
  });

  const [standardFolders, setStandardFolders] = useState<
    Record<string, StandardFolder[]>
  >({});
  const [selectedStandard, setSelectedStandard] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);

  const [activeAssessments, setActiveAssessments] = useState<
    AssessmentStatusResponse[]
  >([]);
  const [failedAssessments, setFailedAssessments] = useState<
    AssessmentStatusResponse[]
  >([]);
  const [reports, setReports] = useState<ComplianceReport[]>([]);

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const [downloadingActionId, setDownloadingActionId] = useState<string | null>(
    null,
  );
  const [showFolderSelection, setShowFolderSelection] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assessmentError, setAssessmentError] = useState<ViewError | null>(
    null,
  );
  const [downloadErrors, setDownloadErrors] = useState<Record<string, string>>(
    {},
  );

  const [expandedStandards, setExpandedStandards] = useState<
    Record<string, boolean>
  >({});
  const [visibleAssessmentCounts, setVisibleAssessmentCounts] = useState<
    Record<string, number>
  >({});

  const standards = useMemo(
    () => Object.keys(standardFolders).sort(),
    [standardFolders],
  );

  const selectedStandardActive =
    selectedStandard &&
    activeAssessments.some(
      a =>
        a.standard === selectedStandard &&
        (a.status === 'REQUESTED' ||
          a.status === 'IN_PROGRESS' ||
          a.status === 'WAITING_FOR_CHECKLIST'),
    );

  const getBaseUrl = useCallback(
    () => discoveryApi.getBaseUrl('business-applications'),
    [discoveryApi],
  );

  const fetchReports = useCallback(async (): Promise<ComplianceReport[]> => {
    const baseUrl = await getBaseUrl();
    const response = await fetchApi.fetch(
      `${baseUrl}/compliance-assessment/reports/${encodeURIComponent(ban)}`,
    );

    if (!response.ok) {
      const message = await getErrorMessage(
        response,
        'Unable to load assessment history.',
      );
      throw new Error(message);
    }

    const data = (await response.json()) as ComplianceReportsResponse;
    const receivedReports = [...(data.reports ?? [])];

    receivedReports.sort(
      (left, right) =>
        new Date(right.timestamp).getTime() -
        new Date(left.timestamp).getTime(),
    );

    setReports(receivedReports);
    setActiveAssessments(data.active_assessments ?? []);
    setFailedAssessments(data.failed_assessments ?? []);
    return receivedReports;
  }, [ban, fetchApi, getBaseUrl]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const baseUrl = await getBaseUrl();

      const foldersResponse = await fetchApi.fetch(
        `${baseUrl}/compliance-assessment/${encodeURIComponent(
          ban,
        )}/standard-folders`,
      );

      if (!foldersResponse.ok) {
        const message = await getErrorMessage(
          foldersResponse,
          'Unable to load available standards.',
        );
        throw new Error(message);
      }

      const foldersData =
        (await foldersResponse.json()) as StandardFoldersResponse;

      setStandardFolders(foldersData.standard_folders ?? {});

      try {
        await fetchReports();
      } catch {
        // Standards can still be used if assessment history is unavailable.
      }
    } catch (loadErrorValue) {
      setLoadError(
        loadErrorValue instanceof Error
          ? loadErrorValue.message
          : 'Unable to load compliance assessment information.',
      );
    } finally {
      setLoading(false);
    }
  }, [ban, fetchApi, fetchReports, getBaseUrl]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const fetchAssessmentStatus = useCallback(
    async (requestId: string) => {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(
        `${baseUrl}/compliance-assessment/${encodeURIComponent(
          requestId,
        )}/status`,
      );

      if (!response.ok) {
        const message = await getErrorMessage(
          response,
          'Unable to retrieve the assessment status.',
        );

        throw new Error(message);
      }

      const data = (await response.json()) as AssessmentStatusResponse;

      if (data.status === 'COMPLETED') {
        setAssessmentError(null);
      }

      return data;
    },
    [fetchApi, getBaseUrl],
  );

  const pollableRequestIds = useMemo<string[]>(
    () =>
      activeAssessments
        .filter(
          assessment =>
            assessment.status === 'REQUESTED' ||
            assessment.status === 'IN_PROGRESS' ||
            assessment.status === 'WAITING_FOR_CHECKLIST',
        )
        .map(assessment => assessment.request_id)
        .sort(),
    [activeAssessments],
  );

  const pollableRequestIdsKey = pollableRequestIds.join(',');

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    if (pollableRequestIdsKey.length === 0) {
      return () => {
        cancelled = true;
      };
    }

    const requestIds: string[] = pollableRequestIdsKey.split(',');

    const poll = async (): Promise<void> => {
      let nextPollInterval = IN_PROGRESS_POLL_INTERVAL_MS;

      try {
        const updated: AssessmentStatusResponse[] = await Promise.all(
          requestIds.map((requestId: string) =>
            fetchAssessmentStatus(requestId),
          ),
        );

        if (cancelled) {
          return;
        }

        if (updated.some(assessment => assessment.status === 'REQUESTED')) {
          nextPollInterval = REQUESTED_POLL_INTERVAL_MS;
        } else if (
          updated.some(assessment => assessment.status === 'IN_PROGRESS')
        ) {
          nextPollInterval = IN_PROGRESS_POLL_INTERVAL_MS;
        } else {
          nextPollInterval = CHECKLIST_POLL_INTERVAL_MS;
        }

        const updatedById = new Map<string, AssessmentStatusResponse>(
          updated.map(
            assessment =>
              [assessment.request_id, assessment] as [
                string,
                AssessmentStatusResponse,
              ],
          ),
        );

        const reachedTerminalState = updated.some(
          assessment =>
            assessment.status === 'COMPLETED' || assessment.status === 'FAILED',
        );

        const stillActive = updated.some(
          assessment =>
            assessment.status === 'REQUESTED' ||
            assessment.status === 'IN_PROGRESS' ||
            assessment.status === 'WAITING_FOR_CHECKLIST',
        );

        setActiveAssessments(current =>
          current
            .map(
              assessment =>
                updatedById.get(assessment.request_id) ?? assessment,
            )
            .filter(
              assessment =>
                assessment.status !== 'COMPLETED' &&
                assessment.status !== 'FAILED',
            ),
        );

        if (reachedTerminalState) {
          await fetchReports();
        }

        if (!cancelled && stillActive) {
          timeoutId = window.setTimeout(poll, nextPollInterval);
        }
      } catch {
        if (!cancelled) {
          timeoutId = window.setTimeout(poll, nextPollInterval);
        }
      }
    };

    timeoutId = window.setTimeout(poll, IN_PROGRESS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [pollableRequestIdsKey, fetchAssessmentStatus, fetchReports]);

  const submitAssessment = async (folderId: number) => {
    if (!selectedStandard) return;

    setStarting(true);
    setShowFolderSelection(false);
    setSelectedFolderId(null);

    try {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(
        `${baseUrl}/compliance-assessment/request`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ban: ban,
            standard: selectedStandard,
            jama_folder_id: folderId,
          }),
        },
      );

      if (!response.ok) {
        const message = await getErrorMessage(
          response,
          'Unable to start the compliance assessment.',
        );

        throw new Error(message);
      }

      const data = (await response.json()) as AssessmentRequestResponse;

      setAssessmentError(null);

      const assessment = await fetchAssessmentStatus(data.request_id);

      setActiveAssessments(current => [
        assessment,
        ...current.filter(a => a.request_id !== assessment.request_id),
      ]);
    } catch (requestError) {
      setAssessmentError({
        message:
          requestError instanceof Error
            ? requestError.message
            : 'Unable to start the compliance assessment.',
        stage: 'Starting assessment',
      });
    } finally {
      setStarting(false);
    }
  };

  type HistoryItem =
    | {
        type: 'COMPLETED';
        timestamp: string;
        standard: string;
        report: ComplianceReport;
      }
    | {
        type: 'FAILED';
        timestamp: string;
        standard: string;
        assessment: AssessmentStatusResponse;
      };

  const historyItems = useMemo<HistoryItem[]>(() => {
    const completed = reports.map(report => ({
      type: 'COMPLETED' as const,
      timestamp: report.timestamp,
      standard: report.standard,
      report,
    }));

    const failed = failedAssessments.map(assessment => ({
      type: 'FAILED' as const,
      timestamp:
        assessment.completed_at ??
        assessment.started_at ??
        assessment.requested_at ??
        '',
      standard: assessment.standard,
      assessment,
    }));

    return [...completed, ...failed].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [reports, failedAssessments]);

  const historyByStandard = useMemo(() => {
    return historyItems.reduce<Record<string, HistoryItem[]>>(
      (groups, item) => {
        if (!groups[item.standard]) {
          groups[item.standard] = [];
        }

        groups[item.standard].push(item);
        return groups;
      },
      {},
    );
  }, [historyItems]);

  const handleStartAssessment = () => {
    if (!selectedStandard) {
      setAssessmentError({
        message: 'Select a standard before starting the assessment.',
        stage: 'Standard selection',
      });
      return;
    }

    const folders = standardFolders[selectedStandard] ?? [];

    if (folders.length === 0) {
      setAssessmentError({
        message: `No Jama folders were found for ${selectedStandard}.`,
        stage: 'Resolving Jama folders',
      });
      return;
    }

    if (folders.length === 1) {
      submitAssessment(folders[0].folder_id);
      return;
    }

    setSelectedFolderId(null);
    setShowFolderSelection(true);
  };

  const handleDownloadReport = async (requestId: string, actionId: string) => {
    setDownloadingActionId(actionId);

    setDownloadErrors(current => {
      const next = { ...current };
      delete next[actionId];
      return next;
    });

    try {
      const refreshedReports = await fetchReports();
      const report = refreshedReports.find(
        item => item.request_id === requestId,
      );

      if (!report?.url) {
        throw new Error('A report download URL is not available.');
      }

      const link = document.createElement('a');
      link.href = report.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch (downloadFailure) {
      setDownloadErrors(current => ({
        ...current,
        [actionId]:
          downloadFailure instanceof Error
            ? downloadFailure.message
            : 'Unable to download the report.',
      }));
    } finally {
      setDownloadingActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="card detail-view-card">
        <p className="assessment-results-empty-state">
          Loading compliance assessment information...
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card detail-view-card">
        <div className="detail-view-header">
          <button type="button" className="back-button" onClick={onBack}>
            <span className="back-button-arrow" aria-hidden="true">
              ←
            </span>
            <span className="back-button-text">
              Back to Application Modules
            </span>
          </button>

          <h4 className="detail-view-title">Onsite Compliance Assessment</h4>
        </div>

        <section className="onsite-compliance-load-error" role="alert">
          <h5>
            Compliance assessment is currently unavailable for this BAN. Ensure
            that BAN is linked to a Jama project that contains requirements and
            evidence.
          </h5>

          <p className="onsite-compliance-load-error-message">{loadError}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button type="button" className="back-button" onClick={onBack}>
          <span className="back-button-arrow" aria-hidden="true">
            ←
          </span>
          <span className="back-button-text">Back to Application Modules</span>
        </button>

        <h4 className="detail-view-title">Onsite Compliance Assessment</h4>
      </div>

      {canEdit && (
        <section className="onsite-compliance-start">
          <p>
            Select the compliance standard to assess this application against.
          </p>

          <div className="onsite-compliance-start-fields">
            <select
              className="form-control"
              value={selectedStandard}
              onChange={event => {
                setSelectedStandard(event.target.value);
              }}
              disabled={showFolderSelection || starting}
              aria-label="Compliance standard"
            >
              <option value="">Select a standard</option>

              {standards.map(standard => (
                <option key={standard} value={standard}>
                  {standard}
                </option>
              ))}
            </select>

            {!showFolderSelection && (
              <button
                type="button"
                className="action-button"
                onClick={handleStartAssessment}
                disabled={
                  !selectedStandard ||
                  Boolean(selectedStandardActive) ||
                  starting
                }
              >
                {starting ? 'Starting...' : 'Start assessment'}
              </button>
            )}
          </div>

          {showFolderSelection && (
            <div className="onsite-compliance-folder-selection">
              <h6>Select Jama folder</h6>

              <p>
                Multiple folders match {selectedStandard}. Select the folder to
                use for this assessment.
              </p>

              <select
                className="form-control"
                value={selectedFolderId ?? ''}
                onChange={event => {
                  const value = event.target.value;
                  setSelectedFolderId(value ? Number(value) : null);
                }}
                aria-label="Jama folder"
              >
                <option value="">Select a folder</option>

                {(standardFolders[selectedStandard] ?? []).map(folder => (
                  <option key={folder.folder_id} value={folder.folder_id}>
                    Folder {folder.folder_name}, project {folder.project_name}
                  </option>
                ))}
              </select>

              <div className="inline-actions">
                <button
                  type="button"
                  className="action-button"
                  disabled={selectedFolderId === null || starting}
                  onClick={() => {
                    if (selectedFolderId !== null) {
                      submitAssessment(selectedFolderId);
                    }
                  }}
                >
                  {starting ? 'Starting...' : 'Start assessment'}
                </button>

                <button
                  type="button"
                  className="action-button secondary-button"
                  disabled={starting}
                  onClick={() => {
                    setShowFolderSelection(false);
                    setSelectedFolderId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {assessmentError && (
        <section className="onsite-compliance-error" role="alert">
          <strong>{assessmentError.stage}</strong>
          <p>{assessmentError.message}</p>
        </section>
      )}

      <section className="onsite-compliance-summary">
        <h5>Ongoing assessment requests</h5>

        {activeAssessments.length === 0 ? (
          <div className="onsite-compliance-empty-card">
            <p>No ongoing assessment requests.</p>
          </div>
        ) : (
          <div className="onsite-compliance-active-list">
            {activeAssessments.map(assessment => (
              <article
                key={assessment.request_id}
                className="onsite-compliance-history-entry"
              >
                <div className="onsite-compliance-history-content">
                  <div>
                    <div className="onsite-compliance-history-title">
                      <strong>{assessment.standard}</strong>

                      <span
                        className={`onsite-compliance-status onsite-compliance-status--${assessment.status.toLowerCase()}`}
                      >
                        {statusLabel(assessment.status)}
                      </span>
                    </div>

                    <dl className="evaluation-fields">
                      <div className="evaluation-field">
                        <dt>Requested:</dt>
                        <dd>{formatDate(assessment.requested_at)}</dd>
                      </div>

                      {assessment.last_completed_step && (
                        <div className="evaluation-field">
                          <dt>Latest completed step:</dt>
                          <dd>{assessment.last_completed_step}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="onsite-compliance-history">
        <h5>Assessment reports</h5>

        {historyItems.length === 0 ? (
          <div className="onsite-compliance-empty-card">
            <p>No completed assessments are available.</p>
          </div>
        ) : (
          <div className="onsite-compliance-standard-groups">
            {Object.entries(historyByStandard).map(
              ([standard, standardReports]) => {
                const expanded = Boolean(expandedStandards[standard]);
                const visibleCount = visibleAssessmentCounts[standard] ?? 2;

                const visibleItems = standardReports.slice(0, visibleCount);

                return (
                  <div
                    key={standard}
                    className="onsite-compliance-standard-group"
                  >
                    <button
                      type="button"
                      className="onsite-compliance-standard-toggle"
                      onClick={() =>
                        setExpandedStandards(current => ({
                          ...current,
                          [standard]: !current[standard],
                        }))
                      }
                      aria-expanded={expanded}
                      aria-controls={`assessment-history-${standard}`}
                    >
                      <span>
                        <strong>{standard}</strong>
                        <span className="onsite-compliance-standard-latest">
                          Latest assessment:{' '}
                          {formatDate(standardReports[0].timestamp)}
                        </span>
                      </span>

                      <span className="onsite-compliance-standard-toggle-meta">
                        {standardReports.length}{' '}
                        {standardReports.length === 1
                          ? 'assessment'
                          : 'assessments'}
                        <span aria-hidden="true">{expanded ? '▲' : '▼'}</span>
                      </span>
                    </button>

                    {expanded && (
                      <div
                        id={`assessment-history-${standard}`}
                        className="onsite-compliance-standard-assessments"
                      >
                        {visibleItems.map(item => (
                          <article
                            key={
                              item.type === 'COMPLETED'
                                ? item.report.request_id
                                : item.assessment.request_id
                            }
                            className="onsite-compliance-history-entry"
                          >
                            {item.type === 'COMPLETED' ? (
                              <>
                                <div className="onsite-compliance-history-content">
                                  <div>
                                    <div className="onsite-compliance-history-title">
                                      <strong>
                                        {formatDate(item.report.timestamp)}
                                      </strong>
                                      <span
                                        className={`onsite-compliance-status ${
                                          item.report.reuse_previous_assessment
                                            ? 'onsite-compliance-status--reused'
                                            : 'onsite-compliance-status--completed'
                                        }`}
                                      >
                                        {item.report.reuse_previous_assessment
                                          ? 'Assessment reused'
                                          : 'Completed'}
                                      </span>
                                    </div>

                                    <p>
                                      Requested by{' '}
                                      {item.report.requested_by ||
                                        'unknown user'}
                                    </p>

                                    {item.report.reuse_previous_assessment ? (
                                      <p className="onsite-compliance-reuse-message">
                                        Existing assessment results were reused
                                        because evidence has not changed from
                                        previous assessment.
                                      </p>
                                    ) : (
                                      <ReportDownloadAction
                                        requestId={item.report.request_id}
                                        location="history"
                                        downloadingActionId={
                                          downloadingActionId
                                        }
                                        downloadErrors={downloadErrors}
                                        onDownload={handleDownloadReport}
                                      />
                                    )}
                                  </div>

                                  {!item.report.reuse_previous_assessment &&
                                    item.report.compliance_score !== null &&
                                    item.report.compliance_score !==
                                      undefined && (
                                      <ComplianceScore
                                        score={item.report.compliance_score}
                                      />
                                    )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="onsite-compliance-history-content">
                                  <div>
                                    <div className="onsite-compliance-history-title">
                                      <strong>
                                        {formatDate(item.timestamp)}
                                      </strong>
                                      <span className="onsite-compliance-status onsite-compliance-status--failed">
                                        Failed
                                      </span>
                                    </div>

                                    <p>
                                      Requested by{' '}
                                      {item.assessment.requested_by ||
                                        'unknown user'}
                                    </p>

                                    {item.assessment.error_message && (
                                      <p className="onsite-compliance-failure-message">
                                        {item.assessment.error_message}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </article>
                        ))}

                        {visibleCount < standardReports.length && (
                          <button
                            type="button"
                            className="action-button secondary-button"
                            onClick={() =>
                              setVisibleAssessmentCounts(current => ({
                                ...current,
                                [standard]: visibleCount + 2,
                              }))
                            }
                          >
                            Show more
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              },
            )}
          </div>
        )}
      </section>
    </div>
  );
}
